import { AdvancedSelect, SelectionValue } from '../components/widgets/select';
import { Dialog, useDialogManager } from '../components/widgets/dialog';
import { FormControl } from '../components/widgets/formControl';
import { PrimaryButton } from '../components/widgets/buttons';
import { showNotification } from '../components/widgets/notificationToaster';
import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import config from './config';
import { ButtonContainer, DescriptionContent, InstructionItem, PointedInstructionsList } from './connectDialogStyle';
import { T } from './helpers';
import { CountryOption, useFilteredCountryListForAmazon } from './hooks/useFilteredCountryListForAmazon';

const CustomInstructionList = styled(PointedInstructionsList)`
    margin: 0;
`;

const CustomDescriptionContent = styled(DescriptionContent)`
    margin-bottom: 20px;
`;

const InstructionLabel = styled.p`
    margin: 0 0 16px 0;

    * {
        margin: 0;
    }
`;

const SelectionContainer = styled(FormControl)`
    font-size: 12px;
    margin-bottom: 8px;
`;

const Note = styled.p`
    margin: 0;
`;

interface Instructions {
    text: string | null | HTMLParagraphElement;
}

export const MarketplaceConnectShopDialog: FunctionComponent<{
    platform: string;
    title?: string;
    titleHtml?: React.ReactNode;
    className?: string;
    windowDecorator?: FunctionComponent<any> | undefined;
    size?: 'small' | 'medium' | 'large' | 'xlarge' | 'fullscreen';
}> = (props) => {
    const countriesList = props.platform === 'AMAZON' ? useFilteredCountryListForAmazon() : [];

    const [selectedCountry, setSelectedCountry] = React.useState<string>('');

    const { closeDialog } = useDialogManager();
    const Close = () => {
        closeDialog('marketplace-connect');
    };

    const firstInstructionsBlock: Instructions[] = [
        {
            text: `Select the Amazon marketplace you want to
                   connect
                   from the dropdown menu. `
        },
        {
            text: `Click <strong>Connect</strong> to authorize the integration.`
        }
    ];

    const secondInstructionsBlock: Instructions[] = [
        {
            text: `You can connect <strong>one marketplace at a time.</strong>`
        },
        {
            text: `If you sell in more than one country, just <strong>repeat this step</strong> for each marketplace you'd like to use with Zakeke.`
        }
    ];

    const handleSelectedCountry = (item: SelectionValue<CountryOption>) => {
        if (item != null && !Array.isArray(item)) {
            setSelectedCountry(item.value);
        }
    };

    const handleSaveClick = async () => {
        if (props.platform === 'AMAZON') {
            if (!selectedCountry) {
                showNotification({
                    type: 'danger',
                    message: T._('Country selection is required.', 'Admin'),
                    dismiss: { duration: 5000 }
                });
                return;
            }

            window.location.href = `${config.baseUrl}Admin/E-Commerce/Amazon/Start?country=${encodeURIComponent(selectedCountry)}`;
            return;
        } else if (props.platform === 'ETSY') {
            window.location.href = `${config.baseUrl}Admin/E-Commerce/Etsy/Start`;
            return;
        }
    };

    return (
        <Dialog title={props.title} titleHtml={props.titleHtml} onClose={Close} windowDecorator={props.windowDecorator} size={props.size}>
            {props.platform === 'AMAZON' && (
                <>
                    <CustomDescriptionContent>
                        {firstInstructionsBlock.map((instruction, idx) => (
                            <div key={idx}>
                                <CustomInstructionList key={idx}>
                                    <InstructionItem key={idx} dangerouslySetInnerHTML={{ __html: `${instruction.text} ` }} />
                                </CustomInstructionList>
                            </div>
                        ))}
                    </CustomDescriptionContent>
                    <CustomDescriptionContent>
                        <InstructionLabel dangerouslySetInnerHTML={{ __html: `<strong>PLEASE NOTE</strong>` }} />
                        {secondInstructionsBlock.map((instruction, idx) => (
                            <div key={idx}>
                                <CustomInstructionList key={idx}>
                                    <InstructionItem key={idx} dangerouslySetInnerHTML={{ __html: `${instruction.text} ` }} />
                                </CustomInstructionList>
                            </div>
                        ))}
                    </CustomDescriptionContent>
                    <SelectionContainer label={T._('Pick a marketplace to connect', 'Admin')}>
                        <AdvancedSelect
                            placeholder={T._('Australia', 'Admin')}
                            isMulti={false}
                            options={countriesList}
                            value={countriesList.find((option) => option.value == selectedCountry)}
                            onChange={handleSelectedCountry}
                            menuPosition={'fixed'}
                        />
                    </SelectionContainer>
                </>
            )}
            {props.platform === 'ETSY' && (
                <>
                    <Note dangerouslySetInnerHTML={{ __html: `Click Connect to link your store and start using Zakeke with Etsy.` }} />
                </>
            )}
            <ButtonContainer>
                <PrimaryButton onClick={handleSaveClick}>{T._('Connect', 'Admin')}</PrimaryButton>
            </ButtonContainer>
        </Dialog>
    );
};
